/**
 * /affiliate - customer-portal affiliate dashboard (OCP-490).
 *
 * Visible only to accounts an operator has granted affiliate status (the
 * Sidebar hides the nav item otherwise, and this page redirects a
 * non-affiliate to the overview). Shows the account's ready-to-share
 * `?ref=` link with a copy button and its live referral stats.
 *
 * Earnings + payout history are owned by OCP-384b/384c; this slice shows
 * clearly-labelled placeholders for those sections.
 */
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
  useAffiliate,
  type AffiliateEarnings,
  type AffiliatePayout,
} from '../lib/use-affiliate';
import { useTransientState } from '../lib/useTransientState';
import { fmtDate } from '../lib/format-time';
import { fmtMoney, fmtNumber } from '../lib/format-money';

export function AffiliatePage() {
  const { t } = useTranslation('affiliate');
  const { loading, affiliate, error } = useAffiliate();
  const [copied, setCopied] = useTransientState(false, 1500);

  async function copy(link: string) {
    try {
      await navigator.clipboard?.writeText(link);
      setCopied(true);
    } catch {
      // Clipboard blocked (permissions / insecure context) - no-op.
    }
  }

  if (loading) {
    return <div className="text-sm text-gray-500">{t('loading')}</div>;
  }
  if (error) {
    return (
      <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        {t('loadFailed', { error })}
      </div>
    );
  }
  // Not an affiliate - bounce to the overview (the nav item is hidden too).
  if (!affiliate) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('intro')}</p>
      </div>

      {affiliate.status === 'paused' && (
        <div className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
          {t('paused')}
        </div>
      )}

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('shareLink')}</h2>
        {affiliate.shareLink ? (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="min-w-0 flex-1 truncate rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-sm">
              {affiliate.shareLink}
            </code>
            <button
              type="button"
              onClick={() => void copy(affiliate.shareLink!)}
              className="shrink-0 rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
            >
              {copied ? t('copied') : t('copyLink')}
            </button>
          </div>
        ) : (
          <p className="mt-3 text-sm text-gray-500">
            {t('linkPending')}
          </p>
        )}
        {affiliate.code && (
          <p className="mt-2 text-xs text-gray-500">
            {t('referralCode')} <span className="font-mono">{affiliate.code}</span>
          </p>
        )}
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatTile label={t('referred')} value={affiliate.stats.referred} />
        <StatTile label={t('converted')} value={affiliate.stats.converted} />
      </section>

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('earnings')}</h2>
        <p className="mt-1 text-xs text-gray-500">{t('earningsNote')}</p>
        {affiliate.earnings.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">
            {t('noCommissions')}
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            {affiliate.earnings.map((e) => (
              <div key={e.currency}>
                {affiliate.earnings.length > 1 && (
                  <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                    {e.currency}
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <MoneyTile label={t('held')} cents={e.holdingCents} currency={e.currency} muted />
                  <MoneyTile label={t('eligible')} cents={e.eligibleCents} currency={e.currency} />
                  <MoneyTile label={t('paidOut')} cents={e.paidCents} currency={e.currency} muted />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <PayoutHistory history={affiliate.payoutHistory} paidTotals={affiliate.earnings} />
    </div>
  );
}

/**
 * OCP-493 - payout history: the itemised paid commissions plus the running
 * paid total (from the earnings `paidCents`, so the two always agree).
 */
function PayoutHistory({
  history,
  paidTotals,
}: {
  history: AffiliatePayout[];
  paidTotals: AffiliateEarnings[];
}) {
  const { t } = useTranslation('affiliate');
  const paidCurrencies = paidTotals.filter((t) => t.paidCents > 0);
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('payoutHistory')}</h2>
        {paidCurrencies.length > 0 && (
          <div className="text-sm text-gray-500">
            {t('paidToDate')}{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {paidCurrencies.map((t) => formatMoney(t.paidCents, t.currency)).join(' · ')}
            </span>
          </div>
        )}
      </div>
      {history.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">
          {t('noPayouts')}
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4 font-medium">{t('colPaidOn')}</th>
                <th className="py-2 pr-4 font-medium">{t('colAmount')}</th>
                <th className="py-2 font-medium">{t('colReference')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 dark:border-gray-800/60">
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{formatDate(p.paidAt)}</td>
                  <td className="py-2 pr-4 font-medium tabular-nums text-gray-900 dark:text-gray-100">
                    {formatMoney(p.amountCents, p.currency)}
                  </td>
                  <td className="py-2 font-mono text-xs text-gray-500">{p.payoutRef ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** ISO timestamp -> a short localized date, falling back to the raw date part. */
function formatDate(iso: string): string {
  return fmtDate(iso, iso.slice(0, 10));
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
        {fmtNumber(value)}
      </div>
    </div>
  );
}

function MoneyTile({
  label,
  cents,
  currency,
  muted,
}: {
  label: string;
  cents: number;
  currency: string;
  muted?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          muted ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {formatMoney(cents, currency)}
      </div>
    </div>
  );
}

/** Cents -> currency string in the active portal language; falls back to `<amount> <CODE>` on an unknown code. */
export function formatMoney(cents: number, currency: string): string {
  try {
    return fmtMoney(cents, currency);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}
